import { FC, useMemo, useCallback, useEffect } from 'react';
import { RadioButton } from '@carbon/react';
import { useAtom } from 'jotai';
import searchAtomPair from 'atoms/search-atom';
import { EntityTable } from 'pages/entities/list/entity-table';
import type {
  EntitySearchParams,
  EntitySearchResponse,
  EntitySearchResult,
} from 'pages/entities/list/types';
import { DEFAULT_PAGE_SIZE } from 'utils/page-sizes';

interface Props {
  selectedEntity: EntitySearchResult | null;
  onSelect: (entity: EntitySearchResult | null) => void;
}

const defaultPayload: EntitySearchParams = {
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  sortAttribute: 'name',
  sortDirection: 'ASC',
  includePrimary: false,
  includeAliases: false,
};

export const EntityTablePicker: FC<Props> = ({ selectedEntity, onSelect }) => {
  const { searchAtom } = useMemo(
    () =>
      searchAtomPair<EntitySearchResponse, EntitySearchParams>(
        { href: '/queries/entities' },
        defaultPayload
      ),
    []
  );
  const [searchResult, setSearchPayload] = useAtom(searchAtom);

  useEffect(() => {
    setSearchPayload((current) => ({ ...defaultPayload, ...(current ?? {}) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchChange = useCallback(
    (text: string) => {
      setSearchPayload((current) => ({
        ...defaultPayload,
        ...(current ?? {}),
        page: 1,
        text: text || undefined,
        sortAttribute: undefined,
        sortDirection: undefined,
      }));
    },
    [setSearchPayload]
  );

  const handleSortChange = useCallback(
    (
      sortAttribute: string | undefined,
      sortDirection: 'ASC' | 'DESC' | undefined
    ) => {
      setSearchPayload((current) => ({
        ...defaultPayload,
        ...(current ?? {}),
        page: 1,
        sortAttribute,
        sortDirection,
      }));
    },
    [setSearchPayload]
  );

  const handlePaginationChange = useCallback(
    (page: number, pageSize: number) => {
      setSearchPayload((current) => ({
        ...defaultPayload,
        ...(current ?? {}),
        page,
        pageSize,
      }));
    },
    [setSearchPayload]
  );

  const renderRowAction = useCallback(
    (entity: EntitySearchResult) => (
      <RadioButton
        id={`entity-picker-radio-${entity.id}`}
        name="entity-picker"
        labelText=""
        checked={selectedEntity?.id === entity.id}
        onClick={() => onSelect(entity)}
      />
    ),
    [selectedEntity, onSelect]
  );

  return (
    <EntityTable
      searchResult={searchResult}
      isFilterOpen={false}
      onToggleFilter={() => {}}
      filterCount={0}
      onSearchChange={handleSearchChange}
      onSortChange={handleSortChange}
      onPaginationChange={handlePaginationChange}
      tableId="add-party-entity-picker"
      renderRowAction={renderRowAction}
      hideFilterToggle
    />
  );
};
